package org.solyton.solawi.bid.application.ui.component.organization

import androidx.compose.runtime.*
import org.evoleq.compose.Markup
import org.evoleq.compose.attribute.dataId
import org.evoleq.compose.conditional.When
import org.evoleq.compose.form.Form
import org.evoleq.compose.form.field.Field
import org.evoleq.compose.form.label.Label
import org.evoleq.compose.layout.Horizontal
import org.evoleq.compose.layout.Vertical
import org.evoleq.compose.style.data.device.DeviceType
import org.evoleq.compose.symbols.InfoLi
import org.evoleq.language.Lang
import org.evoleq.language.get
import org.evoleq.language.subComp
import org.evoleq.language.title
import org.evoleq.math.Reader
import org.evoleq.math.Source
import org.evoleq.math.emit
import org.evoleq.math.times
import org.jetbrains.compose.web.attributes.required
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextInput
import org.jetbrains.compose.web.dom.Ul
import org.solyton.solawi.bid.module.control.dropdown.Dropdown
import org.solyton.solawi.bid.module.control.dropdown.DropdownStyles
import org.solyton.solawi.bid.module.control.dropdown.SimpleUpDown
import org.solyton.solawi.bid.module.country.i18n.CountryLangComponent
import org.solyton.solawi.bid.module.country.i18n.CountryLangComponent.Companion.names
import org.solyton.solawi.bid.module.i18n.data.variables
import org.solyton.solawi.bid.module.style.form.fieldDesktopStyle
import org.solyton.solawi.bid.module.style.form.formDesktopStyle
import org.solyton.solawi.bid.module.style.form.formLabelDesktopStyle
import org.solyton.solawi.bid.module.style.form.textInputDesktopStyle
import org.solyton.solawi.bid.module.user.data.address.*
import org.solyton.solawi.bid.module.user.data.api.userprofile.UserProfileToImport
import org.solyton.solawi.bid.module.user.data.profile.UserProfile
import org.solyton.solawi.bid.module.user.data.profile.addresses
import org.solyton.solawi.bid.module.values.Username

@Markup
@Composable
@Suppress("FunctionName", "CyclomaticComplexMethod", "UnusedParameter")
fun UserProfileForm(
    device: Source<DeviceType>,
    inputs: Source<Lang.Block>,
    countryTexts: Source<Lang.Block>,
    username: Username?,
    setUsername: (Username) -> Unit,
    userProfile: UserProfile?,
    setUserProfile: (UserProfile) -> Unit,
    importUserProfile: (UserProfileToImport) -> Unit,
){
    var userProfileState by remember { mutableStateOf(userProfile) }
    Form(formDesktopStyle) {
        val horizontalFieldsStyle: StyleScope.() -> Unit = {
            width(100.percent)
            gap(16.px)
        }

        // User Profile is used everywhere
        val userProfileInputs = inputs * subComp("userProfile")
        var usernameState by remember { mutableStateOf(username?.value) }
        Ul(attrs = {
            style {
                paddingLeft(1.5.em)
            }
        }) {
            When(userProfileState == null ){
                InfoLi { Text("Wenn Sie Ihrer Organisation ein existirerendes Nutzer Konto als Mitgied hinzufügen wollen, dann geben Sie bitte eine Email Adresses ein und clicken 'Ok'") }
                InfoLi { Text("Es ist nicht möglich ein Mitglied ein zweites Mal hinzuzufügen. Der Ok Button wird in diesem Fall deaktiviert.") }
                InfoLi { Text("Sobald dem Nutzer Konto ein Profil zugewiesen wurde, bekommen Sie die Möglichkeit zusätzliche Daten zu bearbeiten.") }
            }
        }

        Horizontal({
            width(100.percent)
            gap(32.px)
        }) {

            Vertical({
                width(50.percent)
                minWidth(0.px)
            }) {
                H3{Text((userProfileInputs * Reader{lang ->lang["formTitle"]}).emit())}
                Field(fieldDesktopStyle) {
                    Label(
                        (userProfileInputs * subComp("username") * title).emit(),
                        id = "username",
                        labelStyle = formLabelDesktopStyle,
                        isRequired = true
                    )
                    TextInput(usernameState ?: "") {
                        required()
                        id("username")
                        dataId("user-profile.form.username-input")
                        style { textInputDesktopStyle() }
                        onInput {
                            usernameState = it.value
                            try {
                                val username = Username(it.value.trim().lowercase())
                                setUsername(username)
                            } catch (_: Exception) {
                                // console.log("Username is not valid")
                            }
                        }
                    }
                }
                Field(fieldDesktopStyle) {
                    Label(
                        (userProfileInputs * subComp("title") * title).emit(),
                        id = "title",
                        labelStyle = formLabelDesktopStyle
                    )
                    TextInput(userProfileState?.title ?: "") {
                        id("title")
                        dataId("user-profile.form.title-input")
                        style { textInputDesktopStyle() }
                        onInput {
                            val newUserProfile =
                                (userProfileState?: UserProfile.default).copy(title = it.value)
                            userProfileState = newUserProfile
                            setUserProfile(newUserProfile)
                        }
                    }
                }

                Field(fieldDesktopStyle) {
                    Label(
                        (userProfileInputs * subComp("firstname") * title).emit(),
                        id = "firstname",
                        labelStyle = formLabelDesktopStyle,
                        isRequired = true
                    )
                    TextInput(userProfileState?.firstname ?: "") {
                        required()
                        id("firstname")
                        dataId("user-profile.form.firstname-input")
                        style { textInputDesktopStyle() }
                        onInput {
                            val newUserProfile =
                                (userProfileState?: UserProfile.default).copy(firstname = it.value)
                            userProfileState = newUserProfile
                            setUserProfile(newUserProfile)
                        }
                    }
                }

                Field(fieldDesktopStyle) {
                    Label(
                        (userProfileInputs * subComp("lastname") * title).emit(),
                        id = "lastname",
                        labelStyle = formLabelDesktopStyle,
                        isRequired = true
                    )
                    TextInput(userProfileState?.lastname ?: "") {
                        required()
                        id("lastname")
                        dataId("user-profile.form.lastname-input")
                        style { textInputDesktopStyle() }
                        onInput {
                            val newUserProfile =
                                (userProfileState?: UserProfile.default).copy(lastname = it.value)
                            userProfileState = newUserProfile
                            setUserProfile(newUserProfile)
                        }
                    }
                }
                Horizontal(horizontalFieldsStyle) {
                    Field({fieldDesktopStyle()}) {
                        Label(
                            (userProfileInputs * subComp("phoneNumber") * title).emit(),
                            id = "phoneNumber",
                            labelStyle = formLabelDesktopStyle
                        )
                        TextInput(userProfileState?.phoneNumber ?: "") {
                            id("phoneNumber")
                            dataId("user-profile.form.phone-number-input")
                            style { textInputDesktopStyle() }
                            onInput {
                                val newUserProfile =
                                    (userProfileState?: UserProfile.default).copy(phoneNumber = it.value)
                                userProfileState = newUserProfile
                                setUserProfile(newUserProfile)
                            }
                        }
                    }

                    Field(fieldDesktopStyle) {
                        Label(
                            (userProfileInputs * subComp("cellPhoneNumber") * title).emit(),
                            id = "phoneNumber1",
                            labelStyle = formLabelDesktopStyle
                        )
                        TextInput(userProfileState?.phoneNumber1 ?: "") {
                            id("phoneNumber1")
                            dataId("user-profile.form.cell-phone-number-input")
                            style { textInputDesktopStyle() }
                            onInput {
                                val newUserProfile =
                                    (userProfileState?: UserProfile.default).copy(phoneNumber1 = it.value)
                                userProfileState = newUserProfile
                                setUserProfile(newUserProfile)
                            }
                        }
                    }
                }
            }

            Vertical({
                width(50.percent)
                minWidth(0.px)
            }) {
                // Address
                val addressInputs = userProfileInputs * subComp("address")
                val address = userProfileState?.addresses?.firstOrNull()

                H3 { Text((addressInputs * title).emit()) }
                Horizontal(horizontalFieldsStyle) {
                    Field(fieldDesktopStyle) {
                        Label(
                            (addressInputs * subComp("recipientName") * title).emit(),
                            id = "recipientName",
                            labelStyle = formLabelDesktopStyle,
                            isRequired = true
                        )
                        TextInput(address?.recipientName ?: "") {
                            required()
                            id("recipientName")
                            dataId("user-profile.form.recipient-name-input")
                            style { textInputDesktopStyle() }
                            onInput {
                                val newUserProfile = (userProfileState?: UserProfile.default).addresses {
                                    val address = userProfileState?.addresses?.firstOrNull()
                                    listOf(
                                        address?.recipientName { it.value } ?: Address.default().recipientName { it.value }
                                    )
                                }
                                userProfileState = newUserProfile
                                setUserProfile(newUserProfile)
                            }
                        }
                    }

                    Field(fieldDesktopStyle) {
                        Label(
                            (addressInputs * subComp("organizationName") * title).emit(),
                            id = "organizationName",
                            labelStyle = formLabelDesktopStyle
                        )
                        TextInput(address?.organizationName ?: "") {
                            id("organizationName")
                            dataId("user-profile.form.organization-name-input")
                            style { textInputDesktopStyle() }
                            onInput {
                                val newUserProfile = (userProfileState?: UserProfile.default).addresses {
                                    val address = userProfileState?.addresses?.firstOrNull()
                                    listOf(
                                        address?.organizationName { it.value } ?: Address.default()
                                            .organizationName { it.value }
                                    )
                                }
                                userProfileState = newUserProfile
                                setUserProfile(newUserProfile)
                            }
                        }
                    }
                }

                Field(fieldDesktopStyle) {
                    Label(
                        (addressInputs * subComp("addressLine1") * title).emit(),
                        id = "addressLine1",
                        labelStyle = formLabelDesktopStyle,
                        isRequired = true
                    )
                    TextInput(address?.addressLine1 ?: "") {
                        required()
                        id("addressLine1")
                        dataId("user-profile.form.address-line1-input")
                        style { textInputDesktopStyle() }
                        onInput {
                            val newUserProfile = (userProfileState?: UserProfile.default).addresses{
                                val address = userProfileState?.addresses?.firstOrNull()
                                listOf(
                                    address?.addressLine1 { it.value }?: Address.default().addressLine1{it.value}
                                )
                            }
                            userProfileState = newUserProfile
                            setUserProfile(newUserProfile)
                        }
                    }
                }

                Field(fieldDesktopStyle) {
                    Label(
                        (addressInputs * subComp("addressLine2") * title).emit(),
                        id = "addressLine2",
                        labelStyle = formLabelDesktopStyle
                    )
                    TextInput(address?.addressLine2 ?: "") {
                        id("addressLine2")
                        dataId("user-profile.form.address-line2-input")
                        style { textInputDesktopStyle() }
                        onInput {
                            val newUserProfile = (userProfileState?: UserProfile.default).addresses{
                                val address = userProfileState?.addresses?.firstOrNull()
                                listOf(
                                    address?.addressLine2 { it.value }?: Address.default().addressLine2{it.value}
                                )
                            }
                            userProfileState = newUserProfile
                            setUserProfile(newUserProfile)
                        }
                    }
                }
                Horizontal(horizontalFieldsStyle) {
                    Field(fieldDesktopStyle) {
                        Label(
                            (addressInputs * subComp("postalCode") * title).emit(),
                            id = "postalCode",
                            labelStyle = formLabelDesktopStyle,
                            isRequired = true
                        )
                        TextInput(address?.postalCode ?: "") {
                            required()
                            id("postalCode")
                            dataId("user-profile.form.postal-code-input")
                            style { textInputDesktopStyle() }
                            onInput {
                                val newUserProfile = (userProfileState?: UserProfile.default).addresses {
                                    val address = userProfileState?.addresses?.firstOrNull()
                                    listOf(
                                        address?.postalCode { it.value } ?: Address.default().postalCode { it.value }
                                    )
                                }
                                userProfileState = newUserProfile
                                setUserProfile(newUserProfile)
                            }
                        }
                    }
                    Field(fieldDesktopStyle) {
                        Label(
                            (addressInputs * subComp("city") * title).emit(),
                            id = "city",
                            labelStyle = formLabelDesktopStyle,
                            isRequired = true
                        )
                        TextInput(address?.city ?: "") {
                            required()
                            id("city")
                            dataId("user-profile.form.city-input")
                            style { textInputDesktopStyle() }
                            onInput {
                                val newUserProfile = (userProfileState?: UserProfile.default).addresses {
                                    val address = userProfileState?.addresses?.firstOrNull()
                                    listOf(
                                        address?.city { it.value } ?: Address.default().city { it.value }
                                    )
                                }
                                userProfileState = newUserProfile
                                setUserProfile(newUserProfile)
                            }
                        }
                    }
                }
                Horizontal(horizontalFieldsStyle) {

                    val countries = (countryTexts * names * variables).emit().filter { it.key in listOf("DE", "AT", "CH") }
                    val countriesMap = countries.associateBy ({
                        it.value
                    }){it.key}
                    val countryStates = (countryTexts * with(CountryLangComponent){
                        statesOrProvinces(address?.countryCode ?: "DE")
                    } * variables).emit()
                    val countryStatesMap = countryStates.associateBy ({
                        it.value
                    }) {it.key}

                    val dropdownStyles = DropdownStyles()
                        .modifyContainerStyle {
                            marginTop(5.px)
                            width(100.percent)
                        }

                    Field(fieldDesktopStyle) {
                        Label(
                            (addressInputs * subComp("countryCode") * title).emit(),
                            id = "countryCode",
                            labelStyle = formLabelDesktopStyle,
                            isRequired = true
                        )
                        Dropdown(
                            options = countriesMap,
                            selected = countries.firstOrNull{it.key == (address?.countryCode?:"DE")}?.value,
                            styles = dropdownStyles,
                            iconContent = {expanded -> SimpleUpDown(expanded) },
                            dataId = "user-profile.form.country-code-dropdown"
                        ) { (_, value) ->
                            val newUserProfile = (userProfileState?: UserProfile.default).addresses {
                                val address = userProfileState?.addresses?.firstOrNull()
                                listOf(
                                    address?.countryCode { value } ?: Address.default().countryCode { value }
                                )
                            }
                            userProfileState = newUserProfile
                            setUserProfile(newUserProfile)
                        }


                    }
                    Field(fieldDesktopStyle) {
                        Label(
                            (addressInputs * subComp("stateOrProvince") * title).emit(),
                            id = "stateOrProvince",
                            labelStyle = formLabelDesktopStyle,
                            isRequired = true
                        )
                        Dropdown(
                            options = countryStatesMap,
                            selected = countryStates.firstOrNull{it.key == (address?.stateOrProvince?:"DE-BW")}?.value,
                            styles = dropdownStyles,
                            iconContent = {expanded -> SimpleUpDown(expanded) },
                            dataId = "user-profile.form.state-or-province-dropdown"
                        ) { (_, value) ->
                            val newUserProfile = (userProfileState?: UserProfile.default).addresses {
                                val address = userProfileState?.addresses?.firstOrNull()
                                listOf(
                                    address?.stateOrProvince { value } ?: Address.default().stateOrProvince { value }
                                )
                            }
                            userProfileState = newUserProfile
                            setUserProfile(newUserProfile)
                        }
                    }
                }
            }
        }
/*
        When(username != null && userProfile == null) {
            // Try to find user profile
            StdButton(
                texts = {"Find Profile"},
                deviceType = device,
                disabled =  usernameState == null || userProfileState == null || userProfileState?.addresses?.isEmpty()?:false,
                styles = {},
                dataId = "updateProfileButton",
            ) {
                scope.launch {
                    actions dispatch readUserProfiles()
                }
            }
        }
*/
        // var showUpdateProfileButton by remember {mutableStateOf(true)}
        /*
        if(( username == null || userProfile == null)) {
            StdButton(
                texts = {"Create Profile"},
                deviceType = device,
                disabled =  usernameState == null || userProfileState == null || userProfileState?.addresses?.isEmpty()?:false,
                styles = {},
                dataId = "updateProfileButton",
            ) {
                val un = requireNotNull(usernameState) { "Username is null" }
                val up = requireNotNull(userProfileState) { "User profile is null" }
                scope.launch {

                    importUserProfile(UserProfileToImport(
                        username = un,
                        firstName = up.firstname,
                        lastName = up.lastname,
                        title = up.title,
                        phoneNumber = up.phoneNumber,
                        phoneNumber1 = up.phoneNumber1,
                        address = with(up.addresses.first()) {
                            CreateAddress(
                                recipientName = recipientName,
                                organizationName = organizationName,
                                addressLine1 = addressLine1,
                                addressLine2 = addressLine2,
                                city = city,
                                stateOrProvince = stateOrProvince,
                                postalCode = postalCode,
                                countryCode = countryCode
                            )
                        }))
                }
            }
        }

         */
    }
}
